import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users, Search, Plus, Phone, Mail, Shield, MapPin,
  Edit, Trash2, X, Award, Building2, Briefcase, UserCheck
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { agentsAPI, sitesAPI, clientsAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';

/* ========== AGENTS TAB ========== */
const AgentsTab = ({ sites }) => {
  const { toast } = useToast();
  const [agents, setAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '', badge: '', phone: '', email: '',
    speciality: 'surveillance', level: 'junior',
    hireDate: new Date().toISOString().split('T')[0],
    currentSite: '', notes: ''
  });

  const loadAgents = async () => {
    try {
      const response = await agentsAPI.getAll();
      setAgents(response.data || []);
    } catch (error) {
      toast({ title: "Erreur", description: getErrorMessage(error, "Impossible de charger les agents"), variant: "destructive" });
    }
  };

  useEffect(() => { loadAgents(); }, []);

  const filtered = agents.filter(a =>
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.badge?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData({ name: '', badge: '', phone: '', email: '', speciality: 'surveillance', level: 'junior', hireDate: new Date().toISOString().split('T')[0], currentSite: '', notes: '' });
    setEditingAgent(null);
  };

  const openEdit = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name || '', badge: agent.badge || '', phone: agent.phone || '',
      email: agent.email || '', speciality: agent.speciality || 'surveillance',
      level: agent.level || 'junior',
      hireDate: agent.hireDate ? agent.hireDate.split('T')[0] : '',
      currentSite: agent.currentSite || '', notes: agent.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...formData };
      if (!payload.currentSite) delete payload.currentSite;
      if (!payload.notes) delete payload.notes;
      if (editingAgent) {
        await agentsAPI.update(editingAgent.id, payload);
        toast({ title: "Agent modifie", description: `${formData.name} a ete mis a jour` });
      } else {
        await agentsAPI.create(payload);
        toast({ title: "Agent cree", description: `${formData.name} a ete ajoute` });
      }
      setShowModal(false);
      resetForm();
      loadAgents();
    } catch (error) {
      toast({ title: "Erreur", description: getErrorMessage(error, "Impossible de sauvegarder"), variant: "destructive" });
    } finally { setLoading(false); }
  };

  const handleDelete = async (agent) => {
    if (!window.confirm(`Supprimer l'agent ${agent.name} ?`)) return;
    try {
      await agentsAPI.delete(agent.id);
      toast({ title: "Agent supprime", description: `${agent.name} a ete retire` });
      loadAgents();
    } catch (error) {
      toast({ title: "Erreur", description: getErrorMessage(error, "Impossible de supprimer"), variant: "destructive" });
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const statusColors = { active: 'bg-emerald-100 text-emerald-700', inactive: 'bg-slate-100 text-slate-600', leave: 'bg-amber-100 text-amber-700' };
  const statusLabels = { active: 'Actif', inactive: 'Inactif', leave: 'En conge' };
  const levelLabels = { junior: 'Junior', senior: 'Senior', expert: 'Expert' };
  const specialityLabels = { surveillance: 'Surveillance', incendie: 'Securite Incendie', cynophile: 'Cynophile', intervention: 'Intervention', filtrage: 'Filtrage', rondier: 'Rondier' };

  const stats = {
    total: agents.length,
    active: agents.filter(a => a.status === 'active').length,
    leave: agents.filter(a => a.status === 'leave').length,
    inactive: agents.filter(a => a.status === 'inactive').length
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1 w-full">
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-slate-900">{stats.total}</p><p className="text-[11px] text-slate-500">Total</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-emerald-600">{stats.active}</p><p className="text-[11px] text-slate-500">En service</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-amber-600">{stats.leave}</p><p className="text-[11px] text-slate-500">En conge</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-slate-400">{stats.inactive}</p><p className="text-[11px] text-slate-500">Inactifs</p></CardContent></Card>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-cyan-600 hover:bg-cyan-700 flex items-center gap-2 flex-shrink-0" data-testid="add-agent-btn">
          <Plus className="w-4 h-4" /> Nouvel agent
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
        <Input placeholder="Rechercher un agent..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="agent-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((agent) => (
          <Card key={agent.id} className="hover:shadow-md transition-shadow" data-testid={`agent-card-${agent.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">{agent.name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <div><p className="font-semibold text-slate-900 text-sm">{agent.name}</p><p className="text-xs text-slate-500">Badge: {agent.badge}</p></div>
                </div>
                <Badge className={`text-[10px] ${statusColors[agent.status] || statusColors.active}`}>{statusLabels[agent.status] || 'Actif'}</Badge>
              </div>
              <div className="space-y-1.5 text-xs text-slate-600 mb-3">
                <div className="flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-slate-400" /><span>{specialityLabels[agent.speciality] || agent.speciality}</span></div>
                <div className="flex items-center gap-2"><Award className="w-3.5 h-3.5 text-slate-400" /><span>Niveau: {levelLabels[agent.level] || agent.level}</span></div>
                <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" /><span>{agent.phone}</span></div>
                <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" /><span className="truncate">{agent.email}</span></div>
                {agent.currentSite && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400" /><span>{agent.currentSite}</span></div>}
              </div>
              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => openEdit(agent)} data-testid={`edit-agent-${agent.id}`}><Edit className="w-3 h-3 mr-1" /> Modifier</Button>
                <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700 text-xs" onClick={() => handleDelete(agent)} data-testid={`delete-agent-${agent.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12"><Users className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">Aucun agent trouve</p>
          <Button onClick={() => { resetForm(); setShowModal(true); }} variant="outline" className="mt-4">Ajouter un agent</Button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="agent-modal">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader><div className="flex items-center justify-between"><CardTitle>{editingAgent ? 'Modifier l\'agent' : 'Nouvel agent'}</CardTitle><button onClick={() => { setShowModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button></div></CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label htmlFor="name">Nom complet *</Label><Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Jean Dupont" required data-testid="agent-name-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="badge">N de badge *</Label><Input id="badge" name="badge" value={formData.badge} onChange={handleChange} placeholder="AG-001" required data-testid="agent-badge-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label htmlFor="email">Email *</Label><Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="agent@email.com" required data-testid="agent-email-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="phone">Telephone *</Label><Input id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+33 6 12 34 56 78" required data-testid="agent-phone-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label htmlFor="speciality">Specialite</Label>
                    <select id="speciality" name="speciality" value={formData.speciality} onChange={handleChange} className="w-full px-3 py-2 border rounded-md text-sm">
                      <option value="surveillance">Surveillance</option><option value="incendie">Securite Incendie</option><option value="cynophile">Cynophile</option><option value="intervention">Intervention</option><option value="filtrage">Filtrage</option><option value="rondier">Rondier</option>
                    </select></div>
                  <div className="space-y-1.5"><Label htmlFor="level">Niveau</Label>
                    <select id="level" name="level" value={formData.level} onChange={handleChange} className="w-full px-3 py-2 border rounded-md text-sm">
                      <option value="junior">Junior</option><option value="senior">Senior</option><option value="expert">Expert</option>
                    </select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label htmlFor="hireDate">Date d'embauche</Label><Input id="hireDate" name="hireDate" type="date" value={formData.hireDate} onChange={handleChange} /></div>
                  <div className="space-y-1.5"><Label htmlFor="currentSite">Site affecte</Label>
                    <select id="currentSite" name="currentSite" value={formData.currentSite} onChange={handleChange} className="w-full px-3 py-2 border rounded-md text-sm">
                      <option value="">Aucun</option>{sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select></div>
                </div>
                <div className="space-y-1.5"><Label htmlFor="notes">Notes</Label><textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} rows={2} className="w-full px-3 py-2 border rounded-md text-sm" placeholder="Remarques..." /></div>
              </CardContent>
              <div className="flex justify-end gap-2 p-6 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>Annuler</Button>
                <Button type="submit" disabled={loading} className="bg-cyan-600 hover:bg-cyan-700" data-testid="agent-submit-btn">{loading ? 'Enregistrement...' : (editingAgent ? 'Modifier' : 'Creer l\'agent')}</Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

/* ========== CLIENTS TAB ========== */
const ClientsTab = () => {
  const { toast } = useToast();
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '', contactName: '', contactEmail: '', contactPhone: '',
    address: '', city: '', siret: '', type: 'entreprise', notes: ''
  });

  const loadClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data || []);
    } catch (error) {
      toast({ title: "Erreur", description: getErrorMessage(error, "Impossible de charger les clients"), variant: "destructive" });
    }
  };

  useEffect(() => { loadClients(); }, []);

  const filtered = clients.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData({ name: '', contactName: '', contactEmail: '', contactPhone: '', address: '', city: '', siret: '', type: 'entreprise', notes: '' });
    setEditingClient(null);
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '', contactName: client.contactName || '',
      contactEmail: client.contactEmail || '', contactPhone: client.contactPhone || '',
      address: client.address || '', city: client.city || '',
      siret: client.siret || '', type: client.type || 'entreprise',
      notes: client.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingClient) {
        await clientsAPI.update(editingClient.id, formData);
        toast({ title: "Client modifie", description: `${formData.name} mis a jour` });
      } else {
        await clientsAPI.create(formData);
        toast({ title: "Client cree", description: `${formData.name} ajoute avec succes` });
      }
      setShowModal(false);
      resetForm();
      loadClients();
    } catch (error) {
      toast({ title: "Erreur", description: getErrorMessage(error, "Impossible de sauvegarder"), variant: "destructive" });
    } finally { setLoading(false); }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Supprimer le client ${client.name} ?`)) return;
    try {
      await clientsAPI.delete(client.id);
      toast({ title: "Client supprime", description: `${client.name} a ete retire` });
      loadClients();
    } catch (error) {
      toast({ title: "Erreur", description: getErrorMessage(error, "Impossible de supprimer"), variant: "destructive" });
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const typeLabels = { entreprise: 'Entreprise', collectivite: 'Collectivite', commerce: 'Commerce', industrie: 'Industrie', residence: 'Residence', evenement: 'Evenementiel' };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-slate-900">{clients.length}</p><p className="text-[11px] text-slate-500">Total clients</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-emerald-600">{clients.filter(c => c.status === 'active').length}</p><p className="text-[11px] text-slate-500">Actifs</p></CardContent></Card>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-cyan-600 hover:bg-cyan-700 flex items-center gap-2" data-testid="add-client-btn">
          <Plus className="w-4 h-4" /> Nouveau client
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
        <Input placeholder="Rechercher un client..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="client-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((client) => (
          <Card key={client.id} className="hover:shadow-md transition-shadow" data-testid={`client-card-${client.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div><p className="font-semibold text-slate-900 text-sm">{client.name}</p><p className="text-xs text-slate-500">{typeLabels[client.type] || client.type}</p></div>
                </div>
                <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Actif</Badge>
              </div>
              <div className="space-y-1.5 text-xs text-slate-600 mb-3">
                {client.contactName && <div className="flex items-center gap-2"><UserCheck className="w-3.5 h-3.5 text-slate-400" /><span>{client.contactName}</span></div>}
                {client.contactPhone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" /><span>{client.contactPhone}</span></div>}
                {client.contactEmail && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" /><span className="truncate">{client.contactEmail}</span></div>}
                {client.address && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400" /><span>{client.address}{client.city ? `, ${client.city}` : ''}</span></div>}
                {client.siret && <div className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-slate-400" /><span>SIRET: {client.siret}</span></div>}
              </div>
              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => openEdit(client)}><Edit className="w-3 h-3 mr-1" /> Modifier</Button>
                <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700 text-xs" onClick={() => handleDelete(client)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12"><Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">Aucun client trouve</p>
          <Button onClick={() => { resetForm(); setShowModal(true); }} variant="outline" className="mt-4">Ajouter un client</Button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="client-modal">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader><div className="flex items-center justify-between"><CardTitle>{editingClient ? 'Modifier le client' : 'Nouveau client'}</CardTitle><button onClick={() => { setShowModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button></div></CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label htmlFor="c-name">Nom du client *</Label><Input id="c-name" name="name" value={formData.name} onChange={handleChange} placeholder="Carrefour" required data-testid="client-name-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="c-type">Type</Label>
                    <select id="c-type" name="type" value={formData.type} onChange={handleChange} className="w-full px-3 py-2 border rounded-md text-sm">
                      <option value="entreprise">Entreprise</option><option value="collectivite">Collectivite</option><option value="commerce">Commerce</option><option value="industrie">Industrie</option><option value="residence">Residence</option><option value="evenement">Evenementiel</option>
                    </select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label htmlFor="c-contactName">Nom du contact</Label><Input id="c-contactName" name="contactName" value={formData.contactName} onChange={handleChange} placeholder="Pierre Martin" /></div>
                  <div className="space-y-1.5"><Label htmlFor="c-contactPhone">Telephone</Label><Input id="c-contactPhone" name="contactPhone" value={formData.contactPhone} onChange={handleChange} placeholder="+33 1 23 45 67 89" /></div>
                </div>
                <div className="space-y-1.5"><Label htmlFor="c-contactEmail">Email</Label><Input id="c-contactEmail" name="contactEmail" value={formData.contactEmail} onChange={handleChange} placeholder="contact@client.fr" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label htmlFor="c-address">Adresse</Label><Input id="c-address" name="address" value={formData.address} onChange={handleChange} placeholder="1 Rue de la Paix" /></div>
                  <div className="space-y-1.5"><Label htmlFor="c-city">Ville</Label><Input id="c-city" name="city" value={formData.city} onChange={handleChange} placeholder="Paris" /></div>
                </div>
                <div className="space-y-1.5"><Label htmlFor="c-siret">SIRET</Label><Input id="c-siret" name="siret" value={formData.siret} onChange={handleChange} placeholder="123 456 789 00010" /></div>
                <div className="space-y-1.5"><Label htmlFor="c-notes">Notes</Label><textarea id="c-notes" name="notes" value={formData.notes} onChange={handleChange} rows={2} className="w-full px-3 py-2 border rounded-md text-sm" placeholder="Remarques..." /></div>
              </CardContent>
              <div className="flex justify-end gap-2 p-6 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>Annuler</Button>
                <Button type="submit" disabled={loading} className="bg-cyan-600 hover:bg-cyan-700" data-testid="client-submit-btn">{loading ? 'Enregistrement...' : (editingClient ? 'Modifier' : 'Creer le client')}</Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

/* ========== MAIN PAGE ========== */
const Agents = () => {
  const [sites, setSites] = useState([]);

  useEffect(() => {
    const loadSites = async () => {
      try {
        const response = await sitesAPI.getAll();
        setSites(response.data || []);
      } catch (e) { console.error(e); }
    };
    loadSites();
  }, []);

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Gestion RH</h1>
        <p className="text-sm text-slate-500 mt-1">Agents de securite et clients</p>
      </div>

      <Tabs defaultValue="agents" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="agents" className="flex items-center gap-2" data-testid="tab-agents">
            <Users className="w-4 h-4" /> Agents
          </TabsTrigger>
          <TabsTrigger value="clients" className="flex items-center gap-2" data-testid="tab-clients">
            <Building2 className="w-4 h-4" /> Clients
          </TabsTrigger>
        </TabsList>

        <TabsContent value="agents">
          <AgentsTab sites={sites} />
        </TabsContent>

        <TabsContent value="clients">
          <ClientsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Agents;
